import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BaseService } from './base.service';
import { AddressType, Country, Role, SupplierPart } from '../models/masterdata.model';
import { map } from 'rxjs/operators';
import { Language, MethodOfPayment, Entity, Currency, DocumentType, Region, ContextVerbiage, SupplierTax1099Box } from '../models/supplier.model';
import { ToolCategory, ToolSubcategory } from '../models/generic.model';
import { DiversityType, AgencyType, FormTemplate } from '../models/onboarding.model';
import { AppSettings } from '../../app.settings';

@Injectable()
export class MasterDataService extends BaseService {
    constructor(private http: HttpClient,
        private config: AppSettings) {
        super();
    }

    getSupplierContactTypes(): Observable<AddressType[]> {
        const url = `${this.config.BaseUrl}Profile/Masterdata/GetAddressTypes`;
        return this.http.get<AddressType[]>(url, { headers: this.setHeaders() });
    }

    getSupplierContactRoles(): Observable<Role[]> {
        const url = `${this.config.BaseUrl}Profile/Masterdata/GetContactRoles`;
        return this.http.get<Role[]>(url, { headers: this.setHeaders() });
    }

    getCountries(): Observable<Country[]> {
        return this.http.get(`${this.config.BaseUrl}Profile/Masterdata/GetCountries`, { headers: this.setHeaders() })
            .pipe(
                map(res => {
                    return res['items'].map(data => {
                        const obj: Country = {
                            countryID: data.countryID,
                            name: data.name,
                            abbreviation: data.abbreviation,
                            enabled: data.enabled,
                            defaultCurrencyID: data.defaultCurrencyID,
                            regionID: data.regionID
                        };
                        return obj;
                    });
                })
            );
    }

    getLanguages(): Observable<Language[]> {
        return this.http.get<Language[]>(`${this.config.BaseUrl}Profile/Masterdata/GetLanguages`, { headers: this.setHeaders() });
    }

    getSupplierParts(supplierCode: string): Observable<SupplierPart[]> {
        const url = `${this.config.BaseUrl}Profile/MasterData/${supplierCode}/Parts`;
        return this.http.get<SupplierPart[]>(url, { headers: this.setHeaders() });
    }

    getCategories(): Observable<ToolCategory[]> {
        const url = `${this.config.BaseUrl}Profile/Tool/Category`;
        return this.http.get<ToolCategory[]>(url, { headers: this.setHeaders() });
    }

    getSubcategories(supplierCode: string, categoryId: number): Observable<ToolSubcategory[]> {
        const url = `${this.config.BaseUrl}Profile/${supplierCode}/Tool/Category/${categoryId}/Subcategory`;
        return this.http.get<ToolSubcategory[]>(url, { headers: this.setHeaders() });
    }

    getCurrencies(): Observable<Currency[]> {
        return this.http.get<any>(`${this.config.BaseUrl}Profile/Masterdata/Currencies`, { headers: this.setHeaders() })
            .pipe(
                map(res => {
                    return res.map(data => {
                        const obj: Currency = {
                            Id: data.currencyID,
                            Name: data.name,
                            CODE: data.currencyCode
                        };
                        return obj;
                    });
                })
            );
    }

    getDocumentTypes(): Observable<DocumentType[]> {
        return this.http.get<DocumentType[]>(`${this.config.BaseUrl}Profile/Document/Type`, { headers: this.setHeaders() });
    }

    getEmailDomainReasons(): Observable<string[]> {
        const url = `${this.config.BaseUrl}Profile/Masterdata/EmailDomainReasons`;
        return this.http.get<string[]>(url, { headers: this.setHeaders() });
    }

    getEmailDomainContactReasons(): Observable<string[]> {
        const url = `${this.config.BaseUrl}Profile/Masterdata/EmailDomainContactReasons`;
        return this.http.get<string[]>(url, { headers: this.setHeaders() });
    }

    isNoPONoPayPolicyRequired(supplierCode: string): Observable<boolean> {
        const url = `${this.config.BaseUrl}Profile/Masterdata/NoPoNoPayPolicyRequired/${supplierCode}`;
        return this.http.get<any>(url, { headers: this.setHeaders() });
    }

    getRegions(all: string): Observable<Region[]> {
        let params = new HttpParams();
        params = params.set('all', all);
        const url = `${this.config.BaseUrl}Profile/Masterdata/GetRegions`
        return this.http.get<Region[]>(url, { params: params, headers: this.setHeaders() })
            .pipe(
                map(res => {
                    return res.map(data => {
                        const region: Region = {
                            regionID: data.regionID,
                            id: data.id,
                            name: data.name
                        };
                        return region;
                    });
                })
            )
    }

    /** Gets array of diversity types */
    getDiversityTypes(): Observable<DiversityType[]> {
        const url = `${this.config.BaseUrl}Profile/Onboarding/DiversityTypes`;
        return this.http.get<DiversityType[]>(url, { headers: this.setHeaders() }).pipe(
            map(res => res.map((data: any) => {
                const net = new DiversityType();
                net.Id = data.termID;
                net.Name = data.name;
                net.Code = data.code;
                net.Description = data.description;
                return net;
            })
            ));;
    }

    /** Gets array of eligibility types */
    getAgencyDocumentTypes(): Observable<AgencyType[]> {
        const url = `${this.config.BaseUrl}Profile/Onboarding/DiversityCertifications`;
        return this.http.get<AgencyType[]>(url, { headers: this.setHeaders() }).pipe(
            map(res => res.map((data: any) => {
                const net = new AgencyType();
                net.Id = data.termID;
                net.Name = data.name;
                net.Code = data.code;
                net.DocumentType = data.documentType;
                net.Description = data.description;
                net.Type = data.type;
                return net;
            })
            ));;
    }

    /** Sends GET request to get form template */
    getFormTemplate(version:number, formType: string): Observable<FormTemplate> {
        const url = `${this.config.BaseUrl}Profile/Onboarding/GetFormTemplate/${version}/${formType}`;
        return this.http.get<FormTemplate>(url);
    }

    getContextVerbiage(): Observable<ContextVerbiage> {
      const url = `${this.config.BaseUrl}Profile/Onboarding/GetContextVerbiage`;
      var test = this.http.get<ContextVerbiage>(url);
      return test;
    }

    getTax1099Box(): Observable<SupplierTax1099Box[]> {
        const url = `${this.config.BaseUrl}Profile/Tax/AllTax1099Boxes`;
        return this.http.get<any>(url).pipe(
            map(d => {
                return d.map((data: any) => {
                    const tax1099: SupplierTax1099Box = {
                        Id: data.Tax1099BoxID,
                        Name: data.Code,
                        Description: data.Name
                    };
                    return tax1099;
                });
            }));
    }
}
